// @flow
import { REQUEST_ACCOMMODATION_OPTIONS } from '../../PeopleProfile/ProspectInfoTabEditMode/utils';
import { NO_PREFERENCE_KEY } from '../../../utils/noPreference';
import { YES_NO_OPTIONS } from '../../../constants/selectOptions';
import generalMessages from '../../App/messages';
import messages from '../messages';

// The shared lists label their options `text`; RadioGroup reads `label`.
const toRadioItems = (options: Array<Object>) =>
  options.map(({ value, text }) => ({ value, label: text }));

export const buildUnitsAndPreferencesFields = ({
  intl,
  bedroomOptions,
  floorPlanOptions,
  preferenceOptions,
  showAccommodation,
  showDisplacement,
  displacementStatusCodeOptions,
  showPreferences,
  noPreferenceOptionEnabled,
}: {
  intl: Object,
  bedroomOptions: Array<Object>,
  floorPlanOptions: Array<Object>,
  preferenceOptions: Array<Object>,
  showAccommodation: boolean,
  showDisplacement: boolean,
  displacementStatusCodeOptions: Array<Object>,
  showPreferences: boolean,
  noPreferenceOptionEnabled: boolean,
}): Array<Object> => [
  {
    variant: 'multiSelect',
    name: 'bedrooms',
    label: intl.formatMessage(messages.bedrooms),
    options: bedroomOptions,
    noPreferenceOptionEnabled,
    noPreferenceOption: {
      value: NO_PREFERENCE_KEY,
      text: intl.formatMessage(messages.noPreference),
    },
    rules: { required: true },
    GridProps: { xs: 12, sm: 6 },
  },
  {
    // The options arrive already narrowed to the selected bedrooms; watchFields
    // is what makes the rendered select pick the new list up.
    variant: 'singleSelect',
    name: 'floorPlan',
    label: intl.formatMessage(messages.floorPlan),
    options: floorPlanOptions,
    watchFields: ['bedrooms'],
    GridProps: { xs: 12, sm: 6 },
  },
  {
    variant: 'radioGroup',
    name: 'requestAccommodation',
    label: intl.formatMessage(messages.requestAccommodation),
    options: toRadioItems(REQUEST_ACCOMMODATION_OPTIONS),
    row: true,
    rules: { required: true },
    hidden: !showAccommodation,
    GridProps: { xs: 12, sm: 6 },
  },
  {
    variant: 'text',
    name: 'accommodationRequested',
    label: intl.formatMessage(messages.accommodationRequested),
    // A `required` function is resolved once per rules object, and these
    // definitions are memoized, so it would answer with the radio's mount-time
    // value forever. `validate` runs against the live values instead.
    rules: {
      validate: (value, getValues) =>
        getValues('requestAccommodation') !== 'yes' ||
        Boolean(value?.trim()) ||
        intl.formatMessage(generalMessages.required),
    },
    watchFields: ['requestAccommodation'],
    hidden: !showAccommodation,
    GridProps: { xs: 12, sm: 6 },
  },
  {
    variant: 'singleSelect',
    name: 'accessibleUnit',
    label: intl.formatMessage(messages.accessibleUnit),
    options: YES_NO_OPTIONS,
    // The one required field with no `hidden` of its own: every field set asks
    // the question, a conventional household's included.
    rules: { required: true },
    GridProps: { xs: 12, sm: 6 },
  },
  {
    variant: 'singleSelect',
    name: 'displacementStatusCodeId',
    label: intl.formatMessage(messages.displacement),
    options: displacementStatusCodeOptions,
    placeholder: intl.formatMessage(generalMessages.select),
    // Only asked once the vocabulary has arrived. The options come from an
    // org-scoped query that yields `[]` while in flight and on failure, and a
    // required select with no option to pick would refuse the save with nothing
    // the agent could do about it.
    ...(displacementStatusCodeOptions.length ? { rules: { required: true } } : {}),
    hidden: !showDisplacement,
    GridProps: { xs: 12, sm: 6 },
  },
  {
    variant: 'multiSelect',
    name: 'preferences',
    label: intl.formatMessage(messages.preferences),
    options: preferenceOptions,
    hidden: !showPreferences,
    GridProps: { xs: 12 },
  },
];
